import type { ApiClient } from '@daily/contracts/api';
import { uiErrorOf } from './errors.js';
import type { MediaVariant } from './mapping.js';

interface Item { readonly assetId: string; readonly variant: MediaVariant }
interface Cached { readonly url: string; readonly expires: number }

/** Signed media addresses last at most five minutes; the UI re-reads a day or profile after four. */
const LIFETIME_MS = 300_000;
/** An address is handed out again only while it has this long left, so it outlives the UI's re-read. */
const MARGIN_MS = 240_000;
const BATCH = 24;
const KEEP = 256;

/**
 * Short-lived, private media addresses (`POST /api/social/media/access`), batched and kept in memory
 * only. No refresh timers: the UI asks again by re-reading. The backend refuses a whole batch when
 * one item is not readable yet, so a refused batch is split until the readable items are found.
 */
export class MediaAddresses {
  private readonly cache = new Map<string, Cached>();
  private generation = 0;

  constructor(private readonly api: ApiClient, private readonly now: () => number) {}

  url(assetId: string, variant: MediaVariant): string | null {
    const value = this.cache.get(`${assetId}:${variant}`);
    return value && value.expires > this.now() ? value.url : null;
  }

  async ensure(items: readonly Item[]): Promise<void> {
    const generation = this.generation;
    const unique = [...new Map(items.map((item) => [`${item.assetId}:${item.variant}`, item])).values()];
    const needed = unique.filter((item) => { const value = this.cache.get(`${item.assetId}:${item.variant}`); return !value || value.expires - this.now() < MARGIN_MS; });
    const batches: Item[][] = [];
    for (let at = 0; at < needed.length; at += BATCH) batches.push(needed.slice(at, at + BATCH));
    await Promise.all(batches.map((batch) => this.fetch(batch, generation)));
  }

  private async fetch(batch: readonly Item[], generation: number): Promise<void> {
    try {
      const response = await this.api.request('socialMediaAccess', { body: { items: batch.map((item) => ({ assetId: item.assetId, variant: item.variant })) } });
      if (generation !== this.generation) return;
      for (const access of response.items) {
        const expires = Math.min(Date.parse(access.expiresAt), this.now() + LIFETIME_MS);
        if (!Number.isFinite(expires) || expires <= this.now()) continue;
        const key = `${access.assetId}:${access.variant}`;
        this.cache.delete(key);
        this.cache.set(key, { url: access.url, expires });
      }
      while (this.cache.size > KEEP) this.cache.delete(this.cache.keys().next().value ?? '');
    } catch (error) {
      const kind = uiErrorOf(error).kind;
      if (kind === 'auth') throw error;
      if (kind === 'not_found' && batch.length > 1) {
        const half = Math.ceil(batch.length / 2);
        await Promise.all([this.fetch(batch.slice(0, half), generation), this.fetch(batch.slice(half), generation)]);
      }
      // Anything else: these items have no address; the UI shows its broken-media state with Try again.
    }
  }

  clear(): void { this.generation += 1; this.cache.clear(); }
}
